import { type ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useObserver, useSubtree } from "subtree.js/react";
import { ColumnHead } from "../../components/live/ColumnHead";
import { CHAT_COLUMN, PICTURE_FRAME, SIDE_COLUMN } from "../../components/live/cockpit-frame";
import {
  BackIcon,
  CameraIcon,
  EyeIcon,
  KeyIcon,
  MicIcon,
  MicOffIcon,
  MoreIcon,
  RestartIcon,
  StopIcon,
  WarnIcon,
} from "../../components/live/cockpit-icons";
import { SpotGrid } from "../../components/live/SpotGrid";
import { ShareButton, ShareSheet, shareOrOpen } from "../../components/ShareButton";
import { formatCents, formatClock, formatSpan } from "../../core/primitives/live";
import { LineupPicker } from "../../sections/lineup-picker/lineup-picker";
import { type ChatTab, LiveStreamActions, LiveStreamState } from "./live-stream-model";
import { CockpitChat } from "./sections/cockpit-chat/cockpit-chat";
import { Lineup } from "./sections/lineup/lineup";
import { StageCard } from "./sections/stage/stage-card";
import { BoxArt, CycleButton, StreamCycle } from "./sections/stream-cycle/stream-cycle";
import { ViewerPreview } from "./sections/viewer-preview/viewer-preview";

/**
 * The cockpit: chat, the camera, the line-up. One screen, outside the app
 * shell. The `<video>` is the stage's, mounted above the router into the middle
 * column (`PICTURE_FRAME`); this lays the controls over it and the columns
 * beside it. On a phone the picture fills the screen and the line-up is a
 * drawer opened from a handle.
 */
export function LiveStreamPage() {
  const state = useSubtree(LiveStreamState);
  const actions = useSubtree(LiveStreamActions);
  const observer = useObserver();
  const status = observer.watch(state.status);
  const stream = observer.watch(state.stream);
  const over = observer.watch(state.over);
  const lineupOpen = observer.watch(state.lineupOpen);
  const preview = observer.watch(state.preview);
  const chatTab = observer.watch(state.chatTab);
  const breaks = observer.watch(state.breaks);
  const slots = observer.watch(state.slots);
  const totals = observer.watch(state.totals);

  const live = stream?.status === "LIVE";
  const open = slots.filter((s) => s.status === "AVAILABLE").length;
  const summary =
    over && stream?.startedAt && stream.endedAt
      ? `Stream ended · ${formatSpan(stream.endedAt.getTime() - stream.startedAt.getTime())} · ${
          breaks.filter((b) => b.status === "COMPLETED").length
        } boxes ripped`
      : null;

  return (
    /* h-[100dvh], not inset-0. On iOS Safari a `fixed inset-0` box is sized to
       the layout viewport, so anything along the bottom sits behind the toolbar. */
    <div className="fixed inset-x-0 top-0 z-40 h-[100dvh] overflow-hidden text-[#e8ebef]">
      <aside
        className={`absolute inset-y-0 left-0 hidden flex-col border-r border-[color:var(--color-line)] bg-[color:var(--color-app)] lg:flex ${CHAT_COLUMN}`}
      >
        {stream && (
          <>
            <ChatTabs live={live} over={over} />
            <div className="min-h-0 flex-1">
              {live && chatTab === "room" ? (
                <Room />
              ) : (
                <CockpitChat closed={over ? "ended" : live ? null : "prelive"} summary={summary} />
              )}
            </div>
          </>
        )}
      </aside>

      {/* Transparent: the camera renders BELOW this from the stage, and a
          background here would paint over the picture. */}
      <div className={`absolute inset-x-0 top-0 flex h-full flex-col ${PICTURE_FRAME}`}>
        <div className="flex items-start gap-3 px-3.5 pt-[max(1rem,env(safe-area-inset-top))]">
          <div className="flex min-w-0 flex-1 flex-col items-start gap-2.5">
            <StatusPills />
            <Warnings />
            {live && !over && <StageCard />}
            {live && !over && <BoxArt />}
          </div>
          {stream && !over && <Rail />}
        </div>
        <Centre />
        {stream && !over && (
          <>
            {live && (
              <div className="px-4 pb-3 lg:hidden">
                <CockpitChat overlay />
              </div>
            )}
            <section className="flex flex-col gap-2 rounded-t-3xl bg-[#0b0e13] px-[18px] pt-2 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
              <button
                type="button"
                className="flex h-8 items-center justify-between gap-3 text-[13px] font-bold sm:pointer-events-none"
                onClick={actions.toggleLineup}
              >
                <span className="flex items-center gap-2">
                  <span aria-hidden className="h-1 w-8 rounded-full bg-white/25 sm:hidden" />
                  <span className="sm:hidden">
                    Line-up{slots.length > 0 && <span className="font-semibold text-white/55"> · {open} open</span>}
                  </span>
                </span>
                <span className="font-semibold text-white/55 tabular-nums">
                  {formatCents(totals.gross)}
                  {totals.cost > 0 ? ` / ${formatCents(totals.cost)}` : ""} · {totals.sold}/{totals.slots} spots
                </span>
              </button>
              <StreamCycle offAirNote={<ObsLink />} />
            </section>
          </>
        )}
      </div>

      {lineupOpen && (
        <button
          type="button"
          aria-label="Close the line-up"
          className="absolute inset-0 z-10 bg-black/50 sm:hidden"
          onClick={actions.toggleLineup}
        />
      )}
      <aside
        className={`absolute inset-x-0 bottom-0 z-20 max-h-[80dvh] flex-col rounded-t-3xl border-t border-[color:var(--color-line)] bg-[color:var(--color-app)] ${
          lineupOpen ? "flex" : "hidden"
        } sm:inset-y-0 sm:right-0 sm:left-auto sm:z-0 sm:flex sm:max-h-none sm:rounded-none sm:border-t-0 sm:border-l ${SIDE_COLUMN}`}
      >
        <button
          type="button"
          aria-label="Close the line-up"
          className="flex h-6 shrink-0 items-center justify-center sm:hidden"
          onClick={actions.toggleLineup}
        >
          <span className="h-1 w-10 rounded-full bg-white/30" />
        </button>
        {status.kind === "loading" && !stream ? (
          <p className="p-4 text-white/60">Loading the stream…</p>
        ) : status.kind === "error" && !stream ? (
          <p className="note note-danger m-3">{status.message}</p>
        ) : !stream ? null : (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-[env(safe-area-inset-bottom)]">
            {/* The button stays in reach with the drawer up: the drawer is
                where the host is looking mid-rip. */}
            {!over && (
              <div className="px-4 pb-3 sm:hidden">
                <CycleButton />
              </div>
            )}
            {preview ? <PreviewPanel /> : <LineupAndSpots />}
          </div>
        )}
      </aside>

      <CockpitDialog />
      <LineupPicker />
    </div>
  );
}

const PILL =
  "inline-flex h-9 shrink-0 items-center gap-2 rounded-full border border-white/[0.08] bg-[rgba(40,40,40,0.72)] px-3.5 text-sm font-bold whitespace-nowrap tabular-nums";

/** Status in pills over the picture, as the app had it: on air, for how long, who is watching, what is on the wire. */
function StatusPills() {
  const state = useSubtree(LiveStreamState);
  const observer = useObserver();
  const stream = observer.watch(state.stream);
  const over = observer.watch(state.over);
  const watching = observer.watch(state.watching);
  const wire = observer.watch(state.wire);
  const onAir = observer.watch(state.onAir);
  const share = observer.watch(state.share);

  const struggling = wire?.limitedBy
    ? `${wire.limitedBy === "bandwidth" ? "Your upload is struggling" : "This device is struggling"}. Viewers may see a lower picture quality.`
    : undefined;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Link
        to="/live"
        aria-label="Back to Streams"
        className={`${PILL} w-9 justify-center px-0 text-white no-underline`}
      >
        <BackIcon />
      </Link>
      <StatusPill />
      {stream?.status === "LIVE" && !over && (
        <>
          {stream.startedAt && (
            <span className={PILL}>
              <Elapsed since={stream.startedAt} />
            </span>
          )}
          <span className={PILL}>
            <EyeIcon />
            {watching ?? 0}
          </span>
        </>
      )}
      {share && (
        <ShareButton
          target={share}
          heading={over ? "Share the replay" : "Share this stream"}
          note="Viewers watch and grab spots on hobbyshop.ai, or in the HobbyScan app if they have it."
          className={`${PILL} text-white`}
        />
      )}
      {/* What is actually on the wire, not what was asked for: the only way a
          host finds out their "1080p" is going out at 640x360. */}
      {wire && onAir && !over && (
        <span
          title={struggling}
          className={`${PILL} text-[13px] ${wire.limitedBy ? "border-[#a66d1c] bg-[rgba(58,42,16,0.9)] text-[#ffd28a]" : ""}`}
        >
          {wire.limitedBy && <WarnIcon />}
          {wire.resolution} · {wire.fps}
        </span>
      )}
    </div>
  );
}

const cameraSide = (camera: MediaDeviceInfo | undefined) =>
  !camera
    ? null
    : /front|user|facetime/i.test(camera.label)
      ? "Front"
      : /back|rear|environment/i.test(camera.label)
        ? "Back"
        : null;

/** The camera's controls down the right edge, as the app had them. */
function Rail() {
  const state = useSubtree(LiveStreamState);
  const actions = useSubtree(LiveStreamActions);
  const observer = useObserver();
  const cameras = observer.watch(state.cameras);
  const cameraId = observer.watch(state.cameraId);
  const onAir = observer.watch(state.onAir);
  const hasMic = observer.watch(state.hasMic);
  const micMuted = observer.watch(state.micMuted);

  const at = cameras.findIndex((c) => c.deviceId === cameraId);
  const side = cameraSide(cameras[at]);
  const nextCamera = () => {
    const next = cameras[(at + 1) % cameras.length];
    if (next) actions.switchCamera(next.deviceId);
  };

  return (
    <div className="flex shrink-0 flex-col items-center gap-3">
      {cameras.length > 1 && (
        <div className="flex flex-col items-center gap-1">
          <RailButton label="Switch camera" onClick={nextCamera}>
            <CameraIcon size={22} />
          </RailButton>
          {side && (
            <span className="text-[10px] font-extrabold tracking-[0.1em] text-white/85 uppercase [text-shadow:0_1px_3px_rgb(0_0_0/0.9)]">
              {side}
            </span>
          )}
        </div>
      )}
      {onAir && hasMic && (
        <RailButton
          label={micMuted ? "Unmute microphone" : "Mute microphone"}
          active={micMuted}
          onClick={actions.toggleMic}
        >
          {micMuted ? <MicOffIcon /> : <MicIcon />}
        </RailButton>
      )}
      <Menu />
      {onAir && (
        <RailButton label="Stop camera" onClick={actions.stopCamera}>
          <span className="size-[18px] rounded-[3px] bg-[color:var(--color-danger)]" />
        </RailButton>
      )}
    </div>
  );
}

function RailButton({
  label,
  active = false,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={`flex size-[52px] shrink-0 items-center justify-center rounded-full border border-white/[0.08] ${
        active ? "bg-white text-black" : "bg-[rgba(40,40,40,0.72)] text-white hover:bg-[rgba(60,60,60,0.8)]"
      }`}
    >
      {children}
    </button>
  );
}

/** The chat column's header: the conversation, and who is in the room. */
function ChatTabs({ live, over }: { live: boolean; over: boolean }) {
  const state = useSubtree(LiveStreamState);
  const actions = useSubtree(LiveStreamActions);
  const observer = useObserver();
  const tab = observer.watch(state.chatTab);
  const watching = observer.watch(state.watching);
  const current = live ? tab : "chat";

  const item = (id: ChatTab, label: ReactNode) => (
    <button
      type="button"
      role="tab"
      aria-selected={current === id}
      className={`relative flex h-full items-center text-[11px] font-extrabold tracking-[0.12em] uppercase ${
        current === id ? "text-white" : "text-white/45 hover:text-white/75"
      }`}
      onClick={() => actions.selectChatTab(id)}
    >
      {label}
      {current === id && (
        <span aria-hidden className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-[color:var(--color-accent)]" />
      )}
    </button>
  );

  return (
    <div
      role="tablist"
      className="flex h-12 shrink-0 items-center gap-5 border-b border-[color:var(--color-line)] px-4"
    >
      {item("chat", "Chat")}
      {live && item("room", <>In the room · {watching ?? 0}</>)}
      {over && <span className="ml-auto text-xs font-semibold text-white/50">closed</span>}
    </div>
  );
}

/**
 * Who is watching, buyers first with what they hold. Once a box is drawn, the
 * teams each person got, so the host can call them out by name.
 */
function Room() {
  const state = useSubtree(LiveStreamState);
  const observer = useObserver();
  const status = observer.watch(state.viewersStatus);
  const room = observer.watch(state.viewers);
  const watching = observer.watch(state.watching);

  const listed = room?.viewers.length ?? 0;
  const others = Math.max(0, (watching ?? 0) - listed);

  if (status.kind === "error")
    return <p className="p-4 text-[13px] text-[color:var(--color-danger)]">{status.message}</p>;
  if (!room) return <p className="p-4 text-[13px] text-white/50">Loading…</p>;
  if (!room.available) return <p className="p-4 text-[13px] text-white/50">Can't see who is here right now.</p>;

  return (
    <ul className="flex h-full flex-col gap-1 overflow-y-auto p-2">
      {room.viewers.map((v) => {
        const boxes = [...new Set(v.slots.map((s) => s.breakTitle))];
        return (
          <li key={v.userId} className="rounded-lg px-2.5 py-2 hover:bg-white/[0.04]">
            <div className="flex items-baseline gap-2 text-[13px]">
              <span className="min-w-0 flex-1 truncate font-bold text-white">{v.name}</span>
              {v.spots > 0 && (
                <span className="shrink-0 text-xs font-bold text-[color:var(--color-accent)]">
                  {v.spots} spot{v.spots === 1 ? "" : "s"}
                </span>
              )}
            </div>
            {boxes.map((box) => (
              <div key={box} className="mt-1.5 flex flex-wrap items-center gap-1">
                {boxes.length > 1 && <span className="mr-0.5 text-[11px] text-white/40">{box}</span>}
                {v.slots
                  .filter((s) => s.breakTitle === box)
                  .map((s) => (
                    <span
                      key={`${s.breakId}-${s.slotNumber}`}
                      title={`#${s.slotNumber}${s.label ? ` · ${s.label}` : ""}`}
                      className={`max-w-full truncate rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${
                        s.label
                          ? "bg-[color:var(--color-accent)]/15 text-[color:var(--color-accent)]"
                          : "bg-white/[0.06] text-white/60"
                      }`}
                    >
                      {s.label ?? `#${s.slotNumber}`}
                    </span>
                  ))}
              </div>
            ))}
          </li>
        );
      })}
      {listed === 0 && others === 0 && <li className="px-2.5 py-2 text-[13px] text-white/50">Nobody yet.</li>}
      {others > 0 && (
        <li className="px-2.5 py-2 text-xs text-white/45">
          {listed > 0 ? "and " : ""}
          {others} {others === 1 ? "other" : "others"} not signed in
        </li>
      )}
    </ul>
  );
}

function StatusPill() {
  const state = useSubtree(LiveStreamState);
  const observer = useObserver();
  const stream = observer.watch(state.stream);
  const over = observer.watch(state.over);
  const onAir = observer.watch(state.onAir);
  const interrupted = observer.watch(state.interrupted);
  const reconnecting = observer.watch(state.reconnecting);

  if (!stream) return null;
  const pill = `${PILL} uppercase`;
  const grey = `${pill} text-white/60`;

  if (over) {
    const span =
      stream.startedAt && stream.endedAt
        ? ` · ${formatClock(stream.endedAt.getTime() - stream.startedAt.getTime())}`
        : "";
    return <span className={grey}>Ended{span}</span>;
  }
  if (onAir && (interrupted || reconnecting)) {
    return (
      <span className={`${pill} text-[color:var(--color-warn)]`}>
        {interrupted ? "Camera stopped" : "Reconnecting"}
      </span>
    );
  }
  if (stream.status === "LIVE") {
    return (
      <span className={`${pill} text-[color:var(--color-danger)]`}>
        <span className="size-[9px] rounded-full bg-current" />
        {stream.streamPaused ? "Back shortly" : "On air"}
      </span>
    );
  }
  return <span className={grey}>Not live</span>;
}

function Elapsed({ since }: { since: Date }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return <span className="tabular-nums">{formatClock(now - since.getTime())}</span>;
}

function Menu() {
  const state = useSubtree(LiveStreamState);
  const actions = useSubtree(LiveStreamActions);
  const observer = useObserver();
  const isHost = observer.watch(state.isHost);
  const interrupted = observer.watch(state.interrupted);
  const preview = observer.watch(state.preview);
  const share = observer.watch(state.share);
  const over = observer.watch(state.over);
  const [open, setOpen] = useState(false);
  const [sharing, setSharing] = useState(false);

  const item = (
    label: string,
    icon: ReactNode,
    onClick: () => void,
    opts: { danger?: boolean; disabled?: boolean } = {},
  ) => (
    <button
      type="button"
      disabled={opts.disabled}
      className={`flex h-10 w-full items-center gap-2.5 rounded-lg px-3 text-left text-[13px] hover:bg-white/10 disabled:opacity-40 ${
        opts.danger ? "font-bold text-[color:var(--color-danger)]" : "font-semibold"
      }`}
      onClick={() => {
        setOpen(false);
        onClick();
      }}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div className="relative shrink-0">
      <RailButton label="More" active={open} onClick={() => setOpen(!open)}>
        <MoreIcon />
      </RailButton>
      {open && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute top-0 right-[60px] z-20 flex w-60 flex-col gap-0.5 rounded-xl border border-white/10 bg-[#14171c] p-1.5 shadow-[0_16px_40px_rgba(0,0,0,0.6)]">
            {interrupted && item("Restart camera", <RestartIcon />, actions.restartCamera)}
            {share &&
              item(over ? "Share the replay" : "Share stream", <ShareIcon />, () => {
                void shareOrOpen(share, () => setSharing(true));
              })}
            {item("Stream key (OBS)", <KeyIcon />, () => actions.openDialog("encoder"))}
            {item(preview ? "Back to the line-up" : "Preview as buyer", <EyeIcon />, actions.togglePreview)}
            <div className="mx-1.5 my-1 h-px bg-white/10" />
            {item("End stream…", <StopIcon />, () => actions.openDialog("end"), { danger: true, disabled: !isHost })}
          </div>
        </>
      )}
      {sharing && share && (
        <ShareSheet
          target={share}
          heading={over ? "Share the replay" : "Share this stream"}
          note="Viewers watch and grab spots on hobbyshop.ai, or in the HobbyScan app if they have it."
          onClose={() => setSharing(false)}
        />
      )}
    </div>
  );
}

function ShareIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v12M7 8l5-5 5 5M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
    </svg>
  );
}

function Banner({
  tone,
  children,
  action,
}: {
  tone: "warn" | "danger";
  children: ReactNode;
  action?: { label: string; busy?: boolean; onClick: () => void };
}) {
  const look =
    tone === "danger"
      ? "border-[color:var(--color-danger)]/60 bg-[#3a1414] text-[#ffb3b3]"
      : "border-[#a66d1c] bg-[#3a2a10] text-[#ffd28a]";
  return (
    <div className={`flex items-center gap-2.5 rounded-[10px] border px-3.5 py-2.5 text-[13px] font-semibold ${look}`}>
      <WarnIcon />
      <span className="min-w-0 flex-1">{children}</span>
      {action && (
        <button
          type="button"
          disabled={action.busy}
          onClick={action.onClick}
          className="h-[30px] shrink-0 rounded-[7px] border border-current px-3 text-xs font-bold"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

/** Only when something is wrong, and named by the fix. */
function Warnings() {
  const state = useSubtree(LiveStreamState);
  const actions = useSubtree(LiveStreamActions);
  const observer = useObserver();
  const stream = observer.watch(state.stream);
  const over = observer.watch(state.over);
  const isHost = observer.watch(state.isHost);
  const takeOver = observer.watch(state.takeOver);
  const store = observer.watch(state.store);
  const paymentsReady = observer.watch(state.paymentsReady);
  const cameraError = observer.watch(state.cameraError);
  const interrupted = observer.watch(state.interrupted);

  if (!stream || over) return null;

  return (
    <div className="flex w-full flex-col gap-2 empty:hidden">
      {!isHost && (
        <Banner
          tone="warn"
          action={{
            label: takeOver.busy ? "Taking over…" : "Take over hosting",
            busy: takeOver.busy,
            onClick: () => void actions.takeOver(),
          }}
        >
          {stream.host ? `${stream.host.name} is hosting this stream.` : "Someone else is hosting this stream."}
          {takeOver.error && <span className="block text-[color:var(--color-danger)]">{takeOver.error}</span>}
        </Banner>
      )}
      {!paymentsReady && (
        <Banner tone="warn">
          Your shop can't take payments yet.{" "}
          {store && (
            <Link to={`/stores/${store.id}/settings`} className="underline">
              Connect Stripe
            </Link>
          )}
        </Banner>
      )}
      {cameraError && <Banner tone="danger">{cameraError}</Banner>}
      {interrupted && (
        <Banner tone="danger" action={{ label: "Restart camera", onClick: actions.restartCamera }}>
          Camera stopped. Viewers see a frozen picture.
        </Banner>
      )}
      {stream.status === "LIVE" && stream.streamPaused && (
        <Banner tone="warn">No video is arriving. Buyers see "back shortly".</Banner>
      )}
    </div>
  );
}

/** The empty frame before the camera, and what is left after the stream. */
function Centre() {
  const state = useSubtree(LiveStreamState);
  const actions = useSubtree(LiveStreamActions);
  const observer = useObserver();
  const stream = observer.watch(state.stream);
  const over = observer.watch(state.over);
  const phase = observer.watch(state.phase);
  const recording = observer.watch(state.recording);
  const recordingResult = observer.watch(state.recordingResult);

  if (over && stream) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
        <p className="text-[22px] font-extrabold">This stream has ended</p>
        {/* Shipping spans every stream, so it lives on its own page. */}
        <Link to="/shipping" className="btn-primary flex h-14 items-center px-10 text-[17px] font-extrabold">
          Go to Shipping
        </Link>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/[0.07] px-3 py-1.5 text-[13px] font-semibold">
            <span
              className={`size-2 rounded-full ${
                stream.recordingReady ? "bg-[color:var(--color-accent)]" : "bg-[color:var(--color-warn)]"
              }`}
            />
            Recording: {stream.recordingReady ? "ready" : "processing"}
          </span>
          {!stream.recordingReady && (
            <button
              type="button"
              className="btn-ghost btn-sm"
              disabled={recording.busy}
              onClick={actions.checkRecording}
            >
              {recording.busy ? "Checking…" : "Check for the recording"}
            </button>
          )}
        </div>
        {recordingResult && (
          <p className={`note ${recordingResult.found ? "note-accent" : "note-warn"}`}>
            {recordingResult.found
              ? `Found it. ${recordingResult.ordersReady} order${recordingResult.ordersReady === 1 ? "" : "s"} now have a working receipt.`
              : recordingResult.reason}
          </p>
        )}
        {recording.error && <p className="note note-danger">{recording.error}</p>}
      </div>
    );
  }

  if (!stream || phase === "live") return <div className="flex-1" />;

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3.5 px-8 text-center">
      {phase === "connecting" ? (
        <p className="max-w-sm text-sm text-white/70">
          {stream.status === "LIVE" ? "Putting you back on air…" : "Starting your camera…"}
        </p>
      ) : phase === "unsupported" ? (
        <>
          <p className="max-w-sm text-sm text-white/70">This browser cannot broadcast. Try Safari or Chrome.</p>
          <ObsLink />
        </>
      ) : null}
    </div>
  );
}

function ObsLink() {
  const actions = useSubtree(LiveStreamActions);
  return (
    <button
      type="button"
      className="text-xs text-white/45 underline hover:text-white/75"
      onClick={() => actions.openDialog("encoder")}
    >
      Using OBS instead? Get the stream key
    </button>
  );
}

function LineupAndSpots() {
  const state = useSubtree(LiveStreamState);
  const actions = useSubtree(LiveStreamActions);
  const observer = useObserver();
  const breaks = observer.watch(state.breaks);
  const segments = observer.watch(state.segments);
  const over = observer.watch(state.over);
  const shownBreakId = observer.watch(state.shownBreakId);
  const shownBreak = observer.watch(state.shownBreak);
  const slots = observer.watch(state.slots);
  const slotsStatus = observer.watch(state.slotsStatus);
  const open = slots.filter((s) => s.status === "AVAILABLE").length;

  return (
    <>
      <Lineup segments={segments} breaks={breaks} over={over} shownBreakId={shownBreakId} onPick={actions.pickBreak} />

      {shownBreak && (
        <section className="mt-2 border-t border-[color:var(--color-line)]">
          <ColumnHead
            title={
              <>
                Spots · <span className="text-white">{shownBreak.title}</span> ·{" "}
                {formatCents(shownBreak.defaultPriceCents)}
              </>
            }
            aside={shownBreak.randomizedAt ? "teams drawn" : `${open} open`}
          />
          <div className="p-3 pb-4">
            {slotsStatus.kind === "loading" && slots.length === 0 ? (
              <p className="text-sm text-white/60">Loading spots…</p>
            ) : slotsStatus.kind === "error" ? (
              <p className="note note-danger">{slotsStatus.message}</p>
            ) : (
              <SpotGrid slots={slots} priceCents={shownBreak.defaultPriceCents} />
            )}
          </div>
        </section>
      )}
    </>
  );
}

function PreviewPanel() {
  const actions = useSubtree(LiveStreamActions);
  return (
    <>
      <ColumnHead
        title="As a buyer sees it"
        aside={
          <button type="button" className="text-[color:var(--color-accent)]" onClick={actions.togglePreview}>
            Close
          </button>
        }
      />
      <div className="p-3">
        <ViewerPreview />
      </div>
    </>
  );
}

function CockpitDialog() {
  const state = useSubtree(LiveStreamState);
  const actions = useSubtree(LiveStreamActions);
  const observer = useObserver();
  const dialog = observer.watch(state.dialog);
  const end = observer.watch(state.end);
  const endCode = observer.watch(state.endCode);
  const encoder = observer.watch(state.encoder);
  const ingest = observer.watch(state.ingest);

  if (!dialog) return null;

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md space-y-3 rounded-2xl border border-white/10 bg-[#14171c] p-5 shadow-[0_16px_40px_rgba(0,0,0,0.6)]">
        {dialog === "end" ? (
          <>
            <h2 className="text-lg font-extrabold">End the stream?</h2>
            <p className="text-sm text-white/70">
              The recording is finalised, which is what makes buyers' video receipts work. Boxes not broken yet go back
              to your queue and keep selling. A stream cannot be resumed.
            </p>
            {end.error && <p className="note note-warn">{end.error}</p>}
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" className="btn-ghost btn-sm" onClick={() => actions.openDialog(null)}>
                Cancel
              </button>
              {endCode === "breaks_ripping" ? (
                <button
                  type="button"
                  className="btn-destructive btn-sm"
                  disabled={end.busy}
                  onClick={() => actions.endStream(true)}
                >
                  Close the rips and end
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-destructive btn-sm"
                  disabled={end.busy}
                  onClick={() => actions.endStream()}
                >
                  {end.busy ? "Ending…" : "End stream"}
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <h2 className="text-lg font-extrabold">Stream key (OBS)</h2>
            <p className="note text-sm">
              Set your canvas to <span className="font-semibold">1080×1920, vertical</span>. Buyers are on phones held
              upright, and the recording is stuck in whatever shape you start with.
            </p>
            {ingest ? (
              <div className="space-y-2 text-sm">
                <label className="block">
                  <span className="label">Server</span>
                  <input className="field font-mono" readOnly value={ingest.ingestUrl} />
                </label>
                <label className="block">
                  <span className="label">Stream key</span>
                  <input className="field font-mono" readOnly type="password" value={ingest.streamKey} />
                </label>
                <p className="text-xs text-white/50">Anyone holding the key can broadcast to your audience.</p>
              </div>
            ) : (
              <button
                type="button"
                className="btn-secondary btn-sm w-full"
                disabled={encoder.busy}
                onClick={actions.getStreamKey}
              >
                {encoder.busy ? "Setting up…" : "Get my stream key"}
              </button>
            )}
            {encoder.error && <p className="note note-danger">{encoder.error}</p>}
            <div className="flex justify-end">
              <button type="button" className="btn-ghost btn-sm" onClick={() => actions.openDialog(null)}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
